/**
 * Copyright 2024 Phenix Real Time Solutions, Inc. Confidential and Proprietary. All Rights Reserved.
 */
import moment from 'moment';

import fetch from 'services/fetch.service';
import urlPath from 'services/url-path';
import userStore from 'services/user-store';
import {auth} from 'services/auth.service';
import {IPlaylist} from 'services/on-demand.service';
import {transformToPortalError} from 'utility/error-handler';

import {
  IGetMessagesResponse,
  IMessagesParams,
  IRoom,
  IMember
} from 'interfaces';

export const deleteRoom = async(roomId: string, roomType: string): Promise<void> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  return await fetch.delete({
    url: `${urlPath.deleteRoomUrl}/${encodeURIComponent(roomId)}?type=${roomType}`,
    headers
  });
};

export const getAllRoomsState = async(roomIds: string[]): Promise<{roomId: string; isOnline: boolean; multipleStreams: boolean}[]> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const roomsStateResponse = await Promise.all(
    roomIds.map((roomId: string) => fetch.get({
      url: urlPath.stateOfRoomUrl(roomId),
      headers
    }))
  );

  return roomIds.map((roomId, i) => ({
    roomId,
    isOnline: !!+roomsStateResponse[i].data,
    multipleStreams: roomsStateResponse[i].data > 1
  }));
};

export const getRoomMessages = async(roomId: string, params?: IMessagesParams): Promise<{data: IGetMessagesResponse; status: string}> => {
  const defaultMessagesLimit = 100;

  try {
    const queryString = new URLSearchParams({
      ...params,
      limit: String(defaultMessagesLimit)
    }).toString();
    const url = `${urlPath.getRoomMessagesUrl(`${roomId}`)}?${queryString}`;
    const headers = new Headers();

    headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

    const response = await fetch.get({
      url,
      headers
    });

    return response.data;
  } catch (e) {
    const portalError = transformToPortalError(e);

    throw portalError;
  }
};

export const createRoom = async(name: string, alias: string, description: string, type: string): Promise<void> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  return fetch.put({
    url: urlPath.createRoomUrl,
    headers,
    data: {
      room: {
        name,
        alias,
        description,
        type
      }
    }
  });
};

export const fetchRooms = async(type: string): Promise<IRoom[]> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.get({
    url: `${urlPath.fetchRoomsUrl}?type=${type || 'MultiPartyChat'}`,
    headers
  });

  if (!(response.data.rooms instanceof Array)) {
    throw new Error('Incorrect type of response. Should be an array');
  }

  return response.data.rooms.map((room: IRoom) => ({
    ...room,
    created: moment.utc(room.created),
    lastUpdate: moment.utc(room.lastUpdate),
    state: {
      isOnline: false,
      numberOfPrimaryPresenters: 0,
      numberOfAlternatePresenters: 0,
      isHighAvailability: false
    }
  }));
};

export const fetchRoomDetails = async(id: string): Promise<IRoom> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.get({
    url: urlPath.fetchRoomUrl(id),
    headers
  });

  return response.data.room;
};

export const fetchRoomMembers = async(id: string): Promise<IMember[]> => {
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.get({
    url: urlPath.fetchRoomMembersUrl(id),
    headers
  });

  return response.data.members;
};

export const getPlaylistInformation = async(streamId: string): Promise<IPlaylist[]> => { // NOTE: this request requires credentials to be passed. Does not work with token in headers
  const [applicationId, secret] = await Promise.all([
    userStore.get(auth.applicationId),
    userStore.get(auth.secret)
  ]);
  const headers = new Headers();

  headers.set('Authorization', `Basic ${await userStore.get(auth.token)}`);

  const response = await fetch.put({
    url: urlPath.playlistInformation,
    headers,
    data: {
      applicationId,
      secret: atob(secret),
      streamId
    }
  });

  return response.data.playlists || [];
};